import api from './api'
import type { CategoriesResponse, Category } from '../types/category.types'

/**
 * Category Service
 * Handles all category-related API calls
 */
class CategoryService {
  /**
   * Get all categories for a club
   * GET v1/{club}/categories
   */
  async getCategories(clubUuid: string, page: number = 1, perPage: number = 25): Promise<CategoriesResponse> {
    const response = await api.get<CategoriesResponse>(`v1/${clubUuid}/categories`, {
      params: {
        page,
        per_page: perPage
      }
    })
    return response.data
  }

  /**
   * Get single category
   * GET v1/{club}/categories/{category}
   */
  async getCategory(clubUuid: string, categoryUuid: string): Promise<Category> {
    const response = await api.get<Category>(`v1/${clubUuid}/categories/${categoryUuid}`)
    return response.data
  }

  /**
   * Create new category
   * POST v1/{club}/categories
   */
  async createCategory(clubUuid: string, data: Partial<Category>): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/categories`, data)
    return response.data
  }

  /**
   * Update category
   * PUT v1/{club}/categories/{category}
   */
  async updateCategory(clubUuid: string, categoryUuid: string, data: Partial<Category>): Promise<Category> {
    const response = await api.put<Category>(`v1/${clubUuid}/categories/${categoryUuid}`, data)
    return response.data
  }

  /**
   * Delete category
   * DELETE v1/{club}/categories/{category}
   */
  async deleteCategory(clubUuid: string, categoryUuid: string): Promise<any> {
    const response = await api.delete(`v1/${clubUuid}/categories/${categoryUuid}`)
    return response.data
  }

  /**
   * Get coaches for a category
   * GET v1/{club}/categories/{category}/coaches
   */
  async getCategoryCoaches(clubUuid: string, categoryUuid: string): Promise<any[]> {
    const response = await api.get<any[]>(`v1/${clubUuid}/categories/${categoryUuid}/coaches`)
    return response.data
  }

  /**
   * Get players for a category
   * GET v1/{club}/categories/{category}/players
   */
  async getCategoryPlayers(clubUuid: string, categoryUuid: string, page: number = 1, perPage: number = 15): Promise<any> {
    const response = await api.get<any>(`v1/${clubUuid}/categories/${categoryUuid}/players`, {
      params: {
        page,
        per_page: perPage
      }
    })
    return response.data
  }

  /**
   * Get all categories for filters (no pagination, only uuid and name)
   * GET v1/{club}/categories/filters
   */
  async getCategoriesForFilters(clubUuid: string): Promise<Array<{uuid: string, name: string}>> {
    const response = await api.get<Array<{uuid: string, name: string}>>(`v1/${clubUuid}/categories/filters`)
    return response.data
  }

  /**
   * Get all categories with players (for payment form)
   * GET v1/{club}/categories/with-players
   */
  async getCategoriesWithPlayers(clubUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/categories/with-players`)
    return response.data
  }

  /**
   * Get events for a category
   * GET v1/{club}/categories/{category}/events
   */
  async getCategoryEvents(clubUuid: string, categoryUuid: string, dateFrom?: string, dateTo?: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/categories/${categoryUuid}/events`, {
      params: {
        date_from: dateFrom,
        date_to: dateTo
      }
    })
    return response.data
  }
}

export default new CategoryService()
